import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import {
  Chip,
  CircularProgress,
  Collapse,
  Grid, IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Autocomplete } from "@material-ui/lab";
import LinkIcon from "@material-ui/icons/Link";
import { ExpandLess, ExpandMore, LinkOutlined } from "@material-ui/icons";
import { badEmails, fromArrayToOrSentence } from '../../../../helpers/utils';
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import AdminService from "../../services/AdminService";
import RiaContactService from "../../services/RiaContactService";
import InvestorsDomainModal from './InvestorsDomainModal';
import CustomSnackbar from "../../../user/shared/CustomSnackbar";
import EmailTextField from "../../shared/Inputs/EmailTextField";
import ContactInfoPhoneTextField from "../../shared/Inputs/ContactInfoPhoneTextField";
import BlackListDomainModal from './BlackListDomainModal';

// Keep in sync with: app/models/ria_contact.rb
const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,20}([-a-zA-Z0-9@:%_+.~#?&//=!,]*)$/;

const DOB_MONTHS = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' }
];
const DOB_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Keep in sync with: Person::PERSONAL_EMAIL_SOURCES, Person::DOB_SOURCES, and the
// 'manual'/'automatic_from_email'/'import' strings RiaContact writes to contact_first_name_other_source.
const SOURCE_LABELS = {
  manual: 'Manual',
  bulk_import: 'Bulk Import',
  automatic_from_email: 'Automatic from Email',
  import: 'Import',
};

const formatSourceStamp = (source, modifiedDate) => {
  const label = SOURCE_LABELS[source];
  const date = modifiedDate ? moment(modifiedDate).format('MMM D, YYYY h:mm A') : '';
  if (!label) return date;
  return date ? `${label} \u00b7 ${date}` : label;
};
const DOB_CURRENT_YEAR = new Date().getFullYear();
const DOB_MIN_YEAR = DOB_CURRENT_YEAR - 119;
const DOB_YEARS = Array.from({ length: 120 }, (_, i) => DOB_CURRENT_YEAR - i);

const useStyles = makeStyles((theme) => ({
  contentSection: {
    padding: '16px 22px',
    background: '#fff',
    marginBottom: '15px',
    borderRadius: '3px',
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionHeaderTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#000',
  },
  contentSectionContent: {

  },
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  form: {
    height: '100%',
  },
  sourceChip: {
    height: '20px',
    fontSize: '11px',
    fontWeight: 500,
    marginRight: '4px',
    backgroundColor: '#fff',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
  dobSourceChipRow: {
    marginTop: '6px',
  },
  sourceChipManual: {
    color: '#5f6368',
    borderColor: '#c4c7c5',
  },
  sourceChipBulk: {
    color: '#1a73e8',
    borderColor: '#1a73e8',
  },
  field: {
    '& label.Mui-focused': {
      color: '#000',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#000',
    },
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#000',
      },
    },
  },
  tabActive: {
    color: '#1E69E5',
  },
  associationLink: {
    position: 'absolute',
    top: '-10px',
    right: '10px',
    cursor: 'pointer',
    decoration: 'none',
    '&:hover': {
      decoration: 'none',
    }
  },
  inputWithAssociationLink: {
    position: 'relative',
  },
  urlContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const ContactInfo = (props) => {
  const classes = useStyles();
  const {
    contact,
    handleTabChange,
    handleFieldChange,
    hadleOpen,
    handleOnChangeAutocomplete,
    handleInputChangeAutocomplete,
    openTitleAutocomplete,
    openAutomatedTitleAutocomplete,
    openAchievementsAutocomplete,
    loadingAchievements,
    tab,
    onlyTitle,
    onlyEmail,
    onlyLinkedinUrl,
    potentialLinkedinUrl,
    pastEmails,
    setHasFieldChanges,
    emailValidations,
    onInvestorsForDomainChange,
    onInvestorsForDomainEmailFieldChange,
    onValidationStart,
    onValidationEnd,
    onFieldError,
    clearAiDentifiedPersonalEmail
  } = props;

  const { session } = useAdminUserSession();

  const [fileFirstName, setFileFirstName] = useState(contact.file_first_name || '')
  const [fileLastName, setFileLastName] = useState(contact.file_last_name || '')
  const [fileFirstNameOther, setFileFirstNameOther] = useState(contact.file_first_name_other || '')

  const [email, setEmail] = useState(contact.email || '')
  const [secondEmail, setSecondEmail] = useState(contact.secondary_email || '')
  const [linkedinUrl, setLinkedinUrl] = useState(contact.linkedinProfileUrl || '')

  const [officePhoneNumber, setOfficePhoneNumber] = useState(contact.office_phone_number || '');
  const [extOfficePhoneNumber, setExtOfficePhoneNumber] = useState(contact.ext_office_phone_number || '');
  const [countryPrefixOfficePhoneNumber, setCountryPrefixOfficePhoneNumber] = useState(contact.country_prefix_office_phone_number || '');

  const [mobilePhoneNumber, setMobilePhoneNumber] = useState(contact.mobile_phone_number || '');
  const [countryPrefixMobilePhoneNumber, setCountryPrefixMobilePhoneNumber] = useState(contact.country_prefix_mobile_phone_number || '');

  const [additionalPhoneNumber, setAdditionalPhoneNumber] = useState(contact.additional_phone_number || '');
  const [extAdditionalPhoneNumber, setExtAdditionalPhoneNumber] = useState(contact.ext_additional_phone_number || '');
  const [countryPrefixAdditionalPhoneNumber, setCountryPrefixAdditionalPhoneNumber] = useState(contact.country_prefix_additional_phone_number || '');

  const defaultPhoneCountry = (contact.main_office_country_iso || 'US').toLowerCase();

  const [personalTeamPage, setPersonalTeamPage] = useState(contact.contact_bio_url || '')
  const [gender, setGender] = useState(contact.gender || '')
  const [personalEmail, setPersonalEmail] = useState(contact.personal_email || '')
  const [dobMonth, setDobMonth] = useState(contact.dob_month || '')
  const [dobDay, setDobDay] = useState(contact.dob_day || '')
  const [dobYear, setDobYear] = useState(contact.dob_year || '')
  const [dobErrorMessage, setDobErrorMessage] = useState('')
  // handleFieldChange mutates the `contact` prop in place, so the loaded DOB has to be
  // captured at mount to have something stable to compare the on-screen values against.
  const savedDob = useRef({
    dob_month: contact.dob_month || '',
    dob_day: contact.dob_day || '',
    dob_year: contact.dob_year || '',
  })
  const [warningMessage, setWarningMessage] = useState('');
  const [linkedinErrorMessage, setLinkedinErrorMessage] = useState('')
  const [teamPageErrorMessage, setTeamPageErrorMessage] = useState('')
  const [allowedPersonalEmailDomains, setAllowedPersonalEmailDomains] = useState([])
  const [personalEmailErrorMessage, setPersonalEmailErrorMessage] = useState('')

  const [showInvestorsModal, setShowInvestorsModal] = useState(false);
  const [investorsForDomain, setInvestorsForDomain] = useState([]);
  const [showBlackListModal, setShowBlackListModal] = useState(false);
  const [blackListDomains, setBlackListDomains] = useState([]);
  const [blockModalContent, setBlockModalContent] = useState({
    title: 'Domain is blacklisted',
    description: 'This email domain is blacklisted for the associated firm(s) and cannot be used.',
    domainListLabel: 'Blacklisted domain(s):'
  });
  // Tracks which email field (email | secondary_email) triggered the domain validation,
  // so the blacklist/investors modals clear or confirm the correct field.
  const [domainValidationField, setDomainValidationField] = useState('email');

  // Report LinkedIn URL and Team Page URL errors to parent
  useEffect(() => {
    if (onFieldError) onFieldError('linkedinProfileUrl', !!linkedinErrorMessage);
  }, [linkedinErrorMessage]);

  // Date of Birth allows partial dates (Year only, Month+Year, Month+Day), but a
  // lone Month with neither Day nor Year isn't a valid partial date. A Day requires
  // a Month too, but that combo is prevented at the input level (see the Day
  // dropdown's `disabled` prop) rather than flagged here.
  useEffect(() => {
    const hasMonth = !!dobMonth;
    const hasDay = !!dobDay;
    const hasYear = !!dobYear;
    setDobErrorMessage(hasMonth && !hasDay && !hasYear ? 'Select a Day and/or Year, or clear the Month' : '');
  }, [dobMonth, dobDay, dobYear]);

  useEffect(() => {
    if (onFieldError) onFieldError('date_of_birth', !!dobErrorMessage);
  }, [dobErrorMessage]);

  useEffect(() => {
    if (onFieldError) onFieldError('contact_bio_url', !!teamPageErrorMessage);
  }, [teamPageErrorMessage]);

  useEffect(() => {
    AdminService.allowedPersonalEmailDomains(session).then((response) => {
      setAllowedPersonalEmailDomains((response.data.email_domains || []).map((domain) => domain.toLowerCase()));
    }).catch((error) => {
      console.log('Error fetching allowed personal email domains:', error.message);
    });
  }, []);

  // Only check once the allow-list has loaded, so we don't flag a valid domain as
  // disallowed while the list is still in flight.
  useEffect(() => {
    if (!personalEmail || allowedPersonalEmailDomains.length === 0) {
      setPersonalEmailErrorMessage('');
      return;
    }

    const domain = personalEmail.trim().toLowerCase().split('@')[1];
    if (domain && !allowedPersonalEmailDomains.includes(domain)) {
      setPersonalEmailErrorMessage('This email does not have an allowed personal email domain');
    } else {
      setPersonalEmailErrorMessage('');
    }
  }, [personalEmail, allowedPersonalEmailDomains]);

  useEffect(() => {
    if (onFieldError) onFieldError('personal_email_domain', !!personalEmailErrorMessage);
  }, [personalEmailErrorMessage]);

  // Clear error on unmount so a stale domain error doesn't block Save
  useEffect(() => {
    return () => {
      if (onFieldError) onFieldError('personal_email_domain', false);
    };
  }, []);

  // Only stamp the value that's actually saved - while the researcher is mid-edit the
  // stored source/date belong to the previous address, not the one on screen.
  const personalEmailSaved =
    (personalEmail || '').trim().toLowerCase() === (contact.personal_email || '').trim().toLowerCase();
  const personalEmailSourceLabel = personalEmailSaved
    ? SOURCE_LABELS[contact.personal_email_source]
    : null;

  const personalEmailSourceChip = personalEmailSourceLabel ? (
    <Tooltip
      title={formatSourceStamp(contact.personal_email_source, contact.personal_email_modified_date)}
    >
      <Chip
        size="small"
        variant="outlined"
        label={personalEmailSourceLabel}
        className={`${classes.sourceChip} ${
          contact.personal_email_source === 'bulk_import' ? classes.sourceChipBulk : classes.sourceChipManual
        }`}
      />
    </Tooltip>
  ) : null;

  // Only show the source chip while the box matches what's actually saved, and never next to an
  // empty box - same guard as personalEmailSourceChip above.
  const fileFirstNameOtherSaved =
    (fileFirstNameOther || '').trim().toLowerCase() === (contact.file_first_name_other || '').trim().toLowerCase();
  const fileFirstNameOtherSourceLabel = fileFirstNameOtherSaved && fileFirstNameOther
    ? SOURCE_LABELS[contact.contact_first_name_other_source]
    : null;

  // The email an automatic nickname was detected from, which can differ from the current one
  // (e.g. the contact changed firms). An empty title shows no tooltip, so older rows keep the bare chip.
  const fileFirstNameOtherSourceEmailTitle = contact.contact_first_name_other_source === 'automatic_from_email'
    ? (contact.contact_first_name_other_source_email || '')
    : '';

  const fileFirstNameOtherSourceChip = fileFirstNameOtherSourceLabel ? (
    <Tooltip title={fileFirstNameOtherSourceEmailTitle}>
      <Chip
        size="small"
        variant="outlined"
        label={fileFirstNameOtherSourceLabel}
        className={`${classes.sourceChip} ${
          contact.contact_first_name_other_source === 'manual' ? classes.sourceChipManual : classes.sourceChipBulk
        }`}
      />
    </Tooltip>
  ) : null;

  const fileFirstNameOriginText = `Original: ${contact.archived_first_name?.trim() || '(none)'}`;
  const fileLastNameOriginText = `Original: ${contact.archived_last_name?.trim() || '(none)'}`;
  const fileFirstNameOtherOriginText = `Original: ${contact.archived_first_name_other?.trim() || '(none)'}`;

  // dob_source/dob_modified_date stamp the three dob_* fields as one group, so the chip
  // only shows while all three on-screen values still match what's stored - mid-edit the
  // stamp belongs to the previous date, not the one on screen.
  const dobSaved = [
    [dobMonth, savedDob.current.dob_month],
    [dobDay, savedDob.current.dob_day],
    [dobYear, savedDob.current.dob_year],
  ].every(([current, saved]) => String(current || '') === String(saved || ''));
  const dobSourceLabel = dobSaved ? SOURCE_LABELS[contact.dob_source] : null;

  const dobSourceChip = dobSourceLabel ? (
    <Tooltip title={formatSourceStamp(contact.dob_source, contact.dob_modified_date)}>
      <Chip
        size="small"
        variant="outlined"
        label={`DOB: ${dobSourceLabel}`}
        className={`${classes.sourceChip} ${
          contact.dob_source === 'bulk_import' ? classes.sourceChipBulk : classes.sourceChipManual
        }`}
      />
    </Tooltip>
  ) : null;

  const validateEmail = (value, otherEmail, setValue) => {
    if (value === '' || value === undefined || value === null) {
      setValue(value);
      setWarningMessage('')
    } else
      if (badEmails.some(x => value.startsWith(x))) {
        setWarningMessage(`Email is not allowed. It cannot starts with ${fromArrayToOrSentence(badEmails)}`)
      }
      else {
        if (value !== otherEmail) {
          setValue(value);
          setWarningMessage('')
        } else {
          setWarningMessage("Email and Secondary Email must differ")
        }
      }
  }

  const emailFieldSetter = (field) => (field === 'secondary_email' ? setSecondEmail : setEmail);

  // Runs the (free, local) RIA domain allow/black list + personal-domain check BEFORE the
  // real deliverability check (ZeroBounce, costs API credits) is allowed to fire — wired as
  // EmailTextField's `beforeValidate` gate. Returns true to let the deliverability check
  // proceed, false to skip it (domain already blocked, field cleared, modal shown).
  const validateDomainEmailLogic = async (emailValue, field = 'email') => {
    if (emailValue === '') return true;

    try {
      const response = await RiaContactService.validateEmailDomain(
        contact.id,
        session,
        emailValue
      );

      const { status, message, ria_investors, black_list_domains, blocked, reason } = response.data;

      if (status !== 'success') {
        setDomainValidationField(field);
        if (blocked) {
                    // Hard block — show blocking modal (copy depends on why it's blocked), clear the field
                    if (reason === 'personal_domain') {
                      setBlackListDomains([]);
                      setBlockModalContent({
                        title: 'Personal email domain not allowed',
                        description: message || 'Personal email domains (e.g. gmail.com, yahoo.com) are not allowed for this field.',
                        domainListLabel: 'Domain:'
                      });
                    } else if (reason === 'email_in_use') {
                      setBlackListDomains([]);
                      setBlockModalContent({
                        title: 'Email already in use',
                        description: message || 'This email address is already in use by another contact.',
                        domainListLabel: ''
                      });
                    } else {
                      setBlackListDomains(black_list_domains || []);
                      setBlockModalContent({
                        title: 'Domain is blacklisted',
                        description: 'This email domain is blacklisted for the associated firm(s) and cannot be used.',
                        domainListLabel: 'Blacklisted domain(s):'
                      });
                    }
                    setShowBlackListModal(true);
                    clearEmail(field, emailFieldSetter(field));
                    setHasFieldChanges(false);
                    return false;
                } else if (ria_investors && ria_investors.length > 0) {
          setInvestorsForDomain(ria_investors);
          setShowInvestorsModal(true);
          setHasFieldChanges(false);
          return false;
        }
      }

      setHasFieldChanges(false);
      return true;
    } catch (e) {
      setWarningMessage('Validation domain error');
      return true;
    }
  };

  const validateDomainEmail = async (event, email) => {
    event.persist();
    handleFieldChange(event, email);
  }

  const setValidSecondEmail = (value) => {
    validateEmail(value, email, setSecondEmail);
  }

  const setValidFirstEmail = (value) => {
    const hasChanges = !!value
    setHasFieldChanges(hasChanges);
    validateEmail(value, secondEmail, setEmail);
  }

  const handleChangeLinkedinUrl = (event) => {
    if (event.target.value.length > 250) {
      setLinkedinErrorMessage("Url can't be longer than 250 characters")
    } else {
      setLinkedinUrl(event.target.value)
      setLinkedinErrorMessage('')
    }
  }

  const updateField = (name, value) => {
    handleFieldChange({
      target: { name, value, type: 'text' }
    });
  };

  const handleDobMonthChange = (event) => {
    const value = event.target.value;
    setDobMonth(value);
    updateField('dob_month', value);

    // Clearing the Month also clears the Day, since Day requires a Month.
    if (!value && dobDay) {
      setDobDay('');
      updateField('dob_day', '');
    }
  };

  const handleDobDayChange = (event) => {
    const value = event.target.value;
    setDobDay(value);
    updateField('dob_day', value);
  };

  const handleDobYearChange = (event) => {
    const value = event.target.value;
    setDobYear(value);
    updateField('dob_year', value);
  };

  const yearTypeaheadRef = useRef({ buffer: '', lastTime: 0 });

  const handleYearKeyDownCapture = (event) => {
    if (!/^[0-9]$/.test(event.key)) return;

    const now = Date.now();
    const state = yearTypeaheadRef.current;
    state.buffer = (now - state.lastTime > 700 || state.buffer.length >= 4)
      ? event.key
      : state.buffer + event.key;
    state.lastTime = now;

    if (state.buffer.length < 4) return;

    const typedYear = parseInt(state.buffer, 10);
    state.buffer = '';

    if (typedYear < DOB_MIN_YEAR || typedYear > DOB_CURRENT_YEAR) return;

    event.preventDefault();
    event.stopPropagation();

    const yearOption = document.querySelector(`[data-dob-year-option="${typedYear}"]`);
    if (yearOption) {
      yearOption.click();
    } else {
      setDobYear(typedYear);
      updateField('dob_year', typedYear);
    }
  };

  const clearEmail = (name, setter) => {
    handleFieldChange({
      target: { name, value: '', type: 'text' }
    });
    setter('');

    // Keep the AI Dentified personal email in sync: the backend redacts it as soon as
    // this validation comes back invalid, but the stale value would otherwise still be
    // sitting in local state and get sent right back on Save, undoing the redaction.
    if (name === 'personal_email' && clearAiDentifiedPersonalEmail) {
      clearAiDentifiedPersonalEmail();
    }

    setHasFieldChanges(false);
  };

  const handleChangeTeamPage = (event) => {
    const value = event.target.value;
    setPersonalTeamPage(value);

    // Maximum length check
    if (value.length > 250) {
      setTeamPageErrorMessage("URL can't be longer than 250 characters");
      return;
    }

    // URL format check
    if (value && !urlRegex.test(value)) {
      setTeamPageErrorMessage("Wrong URL format");
    } else {
      setTeamPageErrorMessage('');
    }
  };

  const handleCloseInvestorsModal = () => {
    setShowInvestorsModal(false);
    clearEmail(domainValidationField, emailFieldSetter(domainValidationField))
  };

  const handleConfirmInvestorsModal = () => {
    setShowInvestorsModal(false);
    onInvestorsForDomainEmailFieldChange && onInvestorsForDomainEmailFieldChange(domainValidationField);
    onInvestorsForDomainChange(investorsForDomain.map((investor) => investor.crd_id));
  };

  const createPhoneChangeHandler = (phoneFieldName, prefixFieldName, setPhone, setPrefix) => ({ phone, prefix }) => {
    if (phone == '') {
      prefix = ''; // Clear prefix if phone is cleared
    }
    setPhone(phone);
    setPrefix(prefix);

    // Actualizar el campo del teléfono
    handleFieldChange({
      target: {
        name: phoneFieldName,
        value: phone,
        type: 'text'
      }
    });

    // Actualizar el campo del prefijo
    handleFieldChange({
      target: {
        name: prefixFieldName,
        value: prefix,
        type: 'text'
      }
    });
  };

  const handleChangeOfficePhoneNumber = createPhoneChangeHandler(
    'office_phone_number',
    'country_prefix_office_phone_number',
    setOfficePhoneNumber,
    setCountryPrefixOfficePhoneNumber
  );

  const handleChangeMobilePhoneNumber = createPhoneChangeHandler(
    'mobile_phone_number',
    'country_prefix_mobile_phone_number',
    setMobilePhoneNumber,
    setCountryPrefixMobilePhoneNumber
  );

  const handleChangeAdditionalPhoneNumber = createPhoneChangeHandler(
    'additional_phone_number',
    'country_prefix_additional_phone_number',
    setAdditionalPhoneNumber,
    setCountryPrefixAdditionalPhoneNumber
  );

  return (
    <div className={classes.contentSection}>
      <div className={classes.contentSectionHeader} onClick={(event) => handleTabChange(event, 0)}>
        <Typography variant='h5' className={`${classes.contentSectionHeaderTitle} ${tab === 0 ? classes.tabActive : ''}`}>Contact Info</Typography>
        {tab === 0 ? <ExpandLess /> : <ExpandMore />}
      </div>
      <Collapse in={tab === 0}>
        <div className={classes.contentSectionContent}>
          <div className={classes.formGroup}>
            <Grid container spacing={3} >
              {
                !onlyTitle ?
                  <React.Fragment>
                    {(session.isSuperAdmin || session.inHouseResearch) &&
                      <>
                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated First Name"
                            name="file_first_name"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileFirstName(e.target.value)}
                            value={fileFirstName}
                            variant="outlined"
                            className={classes.field}
                            helperText={fileFirstNameOriginText}
                          />
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated Last Name"
                            name="file_last_name"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileLastName(e.target.value)}
                            value={fileLastName}
                            variant="outlined"
                            className={classes.field}
                            helperText={fileLastNameOriginText}
                          />
                        </Grid>

                        <Grid item md={4} xs={12}>
                          <TextField
                            fullWidth
                            label="FINTRX Updated First Name Other"
                            name="file_first_name_other"
                            onBlur={(e) => handleFieldChange(e)}
                            onChange={(e) => setFileFirstNameOther(e.target.value)}
                            value={fileFirstNameOther}
                            variant="outlined"
                            className={classes.field}
                            InputProps={{ endAdornment: fileFirstNameOtherSourceChip }}
                            helperText={fileFirstNameOtherOriginText}
                          />
                        </Grid>

                      </>
                    }

                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <EmailTextField
                        fullWidth
                        label="Email"
                        name="email"
                        onChange={(e) => {
                          setValidFirstEmail(e.target.value);
                          handleFieldChange(e);
                        }}
                        value={email}
                        variant="outlined"
                        className={classes.field}
                        maxLength={40}
                        clearEmail={() => clearEmail('email', setEmail)}
                        validationData={emailValidations?.email}
                        afterOnBlur={(e) => validateDomainEmail(e, email)}
                        beforeValidate={(value) => validateDomainEmailLogic(value, 'email')}
                        entityData={{ entity_id: contact.id, entity_type: "RiaContact", email_field: "email" }}
                        dataSet={'RIA'}
                        onFieldError={onFieldError}
                      />
                    </Grid>

                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <EmailTextField
                        fullWidth
                        label="Secondary Email"
                        name="secondary_email"
                        onChange={(e) => {
                          setValidSecondEmail(e.target.value);
                          handleFieldChange(e);
                        }}
                        value={secondEmail}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        maxLength={40}
                        clearEmail={() => clearEmail('secondary_email', setSecondEmail)}
                        validationData={emailValidations?.secondary_email}
                        afterOnBlur={(e) => validateDomainEmail(e, secondEmail)}
                        beforeValidate={(value) => validateDomainEmailLogic(value, 'secondary_email')}
                        entityData={{ entity_id: contact.id, entity_type: "RiaContact", email_field: "secondary_email" }}
                        dataSet={'RIA'}
                        onFieldError={onFieldError}
                      />
                    </Grid>
                  </React.Fragment>
                  : null
              }
              <Grid
                item
                md={4}
                xs={12}
                className={classes.inputWithAssociationLink}
              >
                {
                  !onlyTitle ?
                    <React.Fragment>
                      {contact.linkedinProfileUrl != '' ? <Link href={contact.linkedinProfileUrl} target='_blank' rel='noreferrer' className={classes.associationLink}><LinkOutlined /></Link> : ''}
                      <TextField
                        fullWidth
                        label="LinkedIn Url"
                        name="linkedinProfileUrl"
                        onChange={handleChangeLinkedinUrl}
                        onBlur={(e) => handleFieldChange(e, linkedinUrl)}
                        value={linkedinUrl}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail}
                        inputProps={{
                          maxLength: 255
                        }}
                        helperText="Please use format https://www.linkedin.com/in/username/"
                      />
                      <span style={{ color: 'red', fontSize: '14px', fontWeight: '400' }}>{linkedinErrorMessage}</span>
                    </React.Fragment>
                    :
                    (contact.linkedinProfileUrl != '' ?
                      <div className={classes.urlContainer}>
                        <span>LinkedIn Url</span>
                        <Link href={contact.linkedinProfileUrl} target='_blank' rel='noreferrer'>
                          {contact.linkedinProfileUrl}
                        </Link>
                      </div>
                      : '')
                }
              </Grid>
              {
                !onlyTitle ?
                  <React.Fragment>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Office Phone Number"
                        name="office_phone_number"
                        prefix={countryPrefixOfficePhoneNumber}
                        value={officePhoneNumber}
                        onChange={handleChangeOfficePhoneNumber}
                        otherPhones={[
                          { phone: mobilePhoneNumber, prefix: countryPrefixMobilePhoneNumber },
                          { phone: additionalPhoneNumber, prefix: countryPrefixAdditionalPhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="EXT Office Phone Number"
                        name="ext_office_phone_number"
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          setExtOfficePhoneNumber(session.isSuperAdmin ? digitsOnly : digitsOnly.slice(0, 6));
                        }}
                        onBlur={(e) => handleFieldChange(e, e.target.value)}
                        value={extOfficePhoneNumber}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        inputProps={session.isSuperAdmin ? {} : { maxLength: 6 }}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Mobile Phone Number"
                        name="mobile_phone_number"
                        prefix={countryPrefixMobilePhoneNumber}
                        value={mobilePhoneNumber}
                        onChange={handleChangeMobilePhoneNumber}
                        otherPhones={[
                          { phone: officePhoneNumber, prefix: countryPrefixOfficePhoneNumber },
                          { phone: additionalPhoneNumber, prefix: countryPrefixAdditionalPhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                        requiredPhoneType="mobile"
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <ContactInfoPhoneTextField
                        fullWidth
                        label="Additional Phone Number"
                        name="additional_phone_number"
                        prefix={countryPrefixAdditionalPhoneNumber}
                        value={additionalPhoneNumber}
                        onChange={handleChangeAdditionalPhoneNumber}
                        otherPhones={[
                          { phone: officePhoneNumber, prefix: countryPrefixOfficePhoneNumber },
                          { phone: mobilePhoneNumber, prefix: countryPrefixMobilePhoneNumber }
                        ]}
                        variant="outlined"
                        onValidationStart={onValidationStart}
                        onValidationEnd={onValidationEnd}
                        onFieldError={onFieldError}
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        defaultCountry={defaultPhoneCountry}
                      />
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="EXT Additional Phone Number"
                        name="ext_additional_phone_number"
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          setExtAdditionalPhoneNumber(session.isSuperAdmin ? digitsOnly : digitsOnly.slice(0, 6));
                        }}
                        onBlur={(e) => handleFieldChange(e, e.target.value)}
                        value={extAdditionalPhoneNumber}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        inputProps={session.isSuperAdmin ? {} : { maxLength: 6 }}
                      />
                    </Grid>
                  </React.Fragment>
                  :
                  null
              }
              {
                !onlyEmail && !onlyLinkedinUrl ?
                  <Grid
                    item
                    md={4}
                    xs={12}
                    className={classes.inputWithAssociationLink}
                  >
                    {/* {<Link to='' onClick={handleOpenNewTitleModal} className={classes.associationLink}><Add /></Link>} */}
                    <Autocomplete
                      id="title_id"
                      open={openTitleAutocomplete}
                      onOpen={(event) => hadleOpen(event, 'openTitleAutocomplete', true)}
                      onClose={(event) => hadleOpen(event, 'openTitleAutocomplete', false)}
                      onChange={(event, value) => handleOnChangeAutocomplete(event, 'titleId', value)}
                      // getOptionSelected={(option, value) => option.id === value.id}
                      getOptionSelected={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name || ''}
                      value={contact.titleDefault}
                      options={contact.titleOptions}
                      className={classes.field}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Title"
                          variant="outlined"
                          onChange={(event) => handleInputChangeAutocomplete(event, 'titles')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {/* {loadingAchievements ? <CircularProgress color="inherit" size={20} /> : null} */}
                                {/*change the loading or remove*/}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  : null
              }
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Email"
                  name="automated_email"
                  value={contact.automated_email}
                  variant="outlined"
                  className={classes.field}
                  maxLength={40}
                  dataSet={'RIA'}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Phone"
                  name="automated_phone"
                  value={contact.automated_phone}
                  variant="outlined"
                  className={classes.field}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <TextField
                  fullWidth
                  label="Automated Linkedin"
                  name="automated_linkedin"
                  value={contact.automated_linkedin}
                  variant="outlined"
                  className={classes.field}
                  maxLength={400}
                  dataSet={'RIA'}
                  disabled // Disabled because this is an automated field
                  InputProps={{
                    endAdornment: <>
                      {
                        !_.isEmpty(contact.automated_source) ?
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="go to site"
                              edge="end"
                            >
                              <Link href={contact.automated_source} target={"_blank"}>
                                <LinkIcon />
                              </Link>
                            </IconButton>
                          </InputAdornment> : null
                      }
                    </>
                  }}
                />
              </Grid>
              {
                !onlyEmail && !onlyLinkedinUrl ?
                  <Grid
                    item
                    md={4}
                    xs={12}
                    className={classes.inputWithAssociationLink}
                  >
                    <TextField
                      fullWidth
                      label="Automated Title"
                      name="automated_title_name"
                      value={contact.automated_title_name}
                      variant="outlined"
                      className={classes.field}
                      disabled // Disabled because this is an automated field
                      InputProps={{
                        endAdornment: <>
                          {
                            !_.isEmpty(contact.automated_source) ?
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="go to site"
                                  edge="end"
                                >
                                  <Link href={contact.automated_source} target={"_blank"}>
                                    <LinkIcon />
                                  </Link>
                                </IconButton>
                              </InputAdornment> : null
                          }
                        </>
                      }}
                    />
                  </Grid>
                  : null
              }
              {
                !onlyTitle && !onlyEmail && !onlyLinkedinUrl ?
                  <React.Fragment>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <TextField
                        select
                        fullWidth
                        value={gender}
                        name='gender'
                        label='Gender'
                        variant='outlined'
                        // onChange={handleFieldChange}
                        onChange={(e) => setGender(e.target.value)}
                        onBlur={(e) => handleFieldChange(e, gender)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        <MenuItem value='Male'>Male</MenuItem>
                        <MenuItem value='Female'>Female</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={12}
                    >
                      <Autocomplete
                        multiple
                        id="achievements_ids"
                        open={openAchievementsAutocomplete}
                        onOpen={(event) => hadleOpen(event, 'openAchievementsAutocomplete', true)}
                        onClose={(event) => hadleOpen(event, 'openAchievementsAutocomplete', false)}
                        onChange={(event, value) => handleOnChangeAutocomplete(event, 'achievementsIds', value)}
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        options={contact.achievementsOptions}
                        value={contact.achievementsDefault}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip classes={{ root: classes.chipRoot, label: classes.chipLabel, deleteIcon: classes.whiteIcon }} label={option.name} {...getTagProps({ index })} />
                          ))
                        }
                        loading={loadingAchievements}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Designations"
                            variant="outlined"
                            onChange={(event) => handleInputChangeAutocomplete(event, 'achievements')}
                            className={classes.field}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loadingAchievements ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </React.Fragment>
                  :
                  null
              }
              <Grid
                item
                md={4}
                xs={12}
                className={classes.inputWithAssociationLink}
              >
                {
                  !onlyTitle ?
                    <React.Fragment>
                      {contact.contact_bio_url != '' ? <Link href={contact.contact_bio_url} target='_blank' rel='noreferrer' className={classes.associationLink}><LinkOutlined /></Link> : ''}
                      <TextField
                        fullWidth
                        label="Personal Team Page"
                        name="contact_bio_url"
                        onChange={handleChangeTeamPage}
                        onBlur={(e) => handleFieldChange(e, contact.contact_bio_url)}
                        value={personalTeamPage}
                        variant="outlined"
                        className={classes.field}
                        disabled={onlyEmail || onlyLinkedinUrl}
                        inputProps={{
                          maxLength: 255
                        }}
                      />
                      <span style={{ color: 'red', fontSize: '14px', fontWeight: '400' }}>{teamPageErrorMessage}</span>
                    </React.Fragment>
                    :
                    (contact.contact_bio_url != '' ?
                      <div className={classes.urlContainer}>
                        <span>Personal Team Page</span>
                        <Link href={contact.contact_bio_url} target='_blank' rel='noreferrer'>
                          {contact.contact_bio_url}
                        </Link>
                      </div>
                      : '')
                }
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <EmailTextField
                  fullWidth
                  label="Personal Email"
                  name="personal_email"
                  onChange={(e) => setPersonalEmail(e.target.value)}
                  value={personalEmail}
                  variant="outlined"
                  className={classes.field}
                  disabled={onlyEmail || onlyLinkedinUrl}
                  maxLength={255}
                  clearEmail={() => clearEmail('personal_email', setPersonalEmail)}
                  afterOnBlur={(e) => handleFieldChange(e)}
                  entityData={{ entity_id: contact.id, entity_type: "RiaContact", email_field: "personal_email" }}
                  dataSet={'RIA'}
                  onFieldError={onFieldError}
                  restrictedDomains={allowedPersonalEmailDomains}
                  restrictedDomainsMessage="This email does not have an allowed personal email domain"
                  extraEndAdornment={personalEmailSourceChip}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      label="Month of Birth"
                      name="dob_month"
                      onChange={handleDobMonthChange}
                      value={dobMonth}
                      variant="outlined"
                      className={classes.field}
                      disabled={onlyEmail || onlyLinkedinUrl}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {DOB_MONTHS.map((month) => (
                        <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      select
                      fullWidth
                      label="Day of Birth"
                      name="dob_day"
                      onChange={handleDobDayChange}
                      value={dobDay}
                      variant="outlined"
                      className={classes.field}
                      disabled={onlyEmail || onlyLinkedinUrl || !dobMonth}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {DOB_DAYS.map((day) => (
                        <MenuItem key={day} value={day}>{day}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={4} onKeyDownCapture={handleYearKeyDownCapture}>
                    <TextField
                      select
                      fullWidth
                      label="Year of Birth"
                      name="dob_year"
                      onChange={handleDobYearChange}
                      value={dobYear}
                      variant="outlined"
                      className={classes.field}
                      disabled={onlyEmail || onlyLinkedinUrl}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {DOB_YEARS.map((year) => (
                        <MenuItem key={year} value={year} data-dob-year-option={year}>{year}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                {dobSourceChip && <div className={classes.dobSourceChipRow}>{dobSourceChip}</div>}
                {dobErrorMessage && <span style={{ color: 'red', fontSize: '14px', fontWeight: '400' }}>{dobErrorMessage}</span>}
              </Grid>
            </Grid>
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            {
              contact.has_bad_emails ?
                <div>
                  <div className='inaccurate'>Bad Emails</div>
                  <div>
                    {pastEmails?.join(', ')}
                  </div>
                </div>
                : null
            }
            {potentialLinkedinUrl &&
              <div>
                <div className='resolved'>Potential LinkedIn URL</div>
                <div>
                  <Link href={potentialLinkedinUrl} target='_blank' rel='noreferrer'>{potentialLinkedinUrl}</Link>
                </div>
              </div>
            }
          </div>
          {
            warningMessage ? <CustomSnackbar
              open={warningMessage}
              closeHandler={() => {
                setWarningMessage('')
              }}
              message={warningMessage}
              options={{
                severity: 'warning'
              }}
            /> : null
          }
        </div>
      </Collapse>

      {showInvestorsModal && (
        <InvestorsDomainModal
          onConfirm={handleConfirmInvestorsModal}
          onClose={handleCloseInvestorsModal}
        />
      )}

      {showBlackListModal && (
        <BlackListDomainModal
          blackListDomains={blackListDomains}
          title={blockModalContent.title}
          description={blockModalContent.description}
          domainListLabel={blockModalContent.domainListLabel}
          onClose={() => setShowBlackListModal(false)}
        />
      )}
    </div>
  )
}

ContactInfo.defaultProps = {
  onlyTitle: false,
  onlyEmail: false,
}

export default ContactInfo;
